var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useForm, Controller, FormProvider, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, LoadingButton, ModalHeader, ModalBody, ModalFooter, Typography, } from '@one-thd/sui-atomic-components';
import { WarningFilled } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getTextFieldStatus } from '../../../utils/form';
import { CardType } from '../../../constants/payments';
import { checkingAccountValidationSchema, giftCardValidationSchema, keytagValidationSchema, } from './validation';
import { getHyphenatedGiftCard } from '../../../utils/general';
export const AddCardModal = ({ cardType, hideModal, onAddCard, isLoading, }) => {
    const headerMap = {
        [CardType.GIFT_CARD]: () => {
            return 'Add Gift Card';
        },
        [CardType.CHECKING_ACCOUNT]: () => {
            return 'Add Checking Account';
        },
        [CardType.KEY_TAG]: () => {
            return 'Add Keytag';
        },
    };
    const nameLabelMap = {
        [CardType.GIFT_CARD]: () => {
            return 'Gift Card Nickname';
        },
        [CardType.CHECKING_ACCOUNT]: () => {
            return 'Account Nickname';
        },
        [CardType.KEY_TAG]: () => {
            return 'Keytag Nickname';
        },
    };
    const numberLabelMap = {
        [CardType.GIFT_CARD]: () => {
            return 'Gift Card Number*';
        },
        [CardType.CHECKING_ACCOUNT]: () => {
            return 'Account Number*';
        },
        [CardType.KEY_TAG]: () => {
            return 'Keytag Number*';
        },
    };
    const buttonMap = {
        [CardType.GIFT_CARD]: () => {
            return 'Add Gift Card';
        },
        [CardType.CHECKING_ACCOUNT]: () => {
            return 'Add Checking Account';
        },
        [CardType.KEY_TAG]: () => {
            return 'Add Keytag';
        },
    };
    const validationMap = {
        [CardType.GIFT_CARD]: () => {
            return giftCardValidationSchema;
        },
        [CardType.CHECKING_ACCOUNT]: () => {
            return checkingAccountValidationSchema;
        },
        [CardType.KEY_TAG]: () => {
            return keytagValidationSchema;
        },
    };
    const childrenMap = {
        [CardType.GIFT_CARD]: () => {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", null,
                    React.createElement(Typography, { variant: "body-base" }, "You can view all purchases made with a saved gift card in your account and receive any associated rebates."))));
        },
        [CardType.CHECKING_ACCOUNT]: () => null,
        [CardType.KEY_TAG]: () => null,
    };
    const formMethods = useForm({
        resolver: yupResolver(validationMap[cardType]()),
        mode: 'onTouched',
    });
    const { channel } = React.useContext(ExperienceContext);
    const isMobile = channel === 'mobile';
    const { control, formState: { errors: formErrors }, handleSubmit, } = formMethods;
    const onSubmitForm = ({ cardNickName, cardNumber, routingNumber, }) => {
        const paymentNumber = typeof cardNumber === 'string'
            ? cardNumber.replace(/-/g, '')
            : cardNumber;
        onAddCard(cardNickName, paymentNumber || '', routingNumber || '');
    };
    const headerText = headerMap[cardType]();
    const nameLabelText = nameLabelMap[cardType]();
    const numberLabelText = numberLabelMap[cardType]();
    const buttonText = buttonMap[cardType]();
    const children = childrenMap[cardType]();
    const iconSize = isMobile ? 'small' : 'regular';
    return (React.createElement(React.Fragment, null,
        React.createElement(FormProvider, Object.assign({}, formMethods),
            React.createElement("form", { onSubmit: handleSubmit(onSubmitForm) },
                React.createElement(ModalHeader, { onClose: hideModal }, headerText),
                React.createElement(ModalBody, null,
                    children,
                    React.createElement("div", { className: "sui-gap-4 sui-py-4 sui-w-full sm:sui-w-auto" },
                        React.createElement(Controller, { name: "cardNickName", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { id: "tenderNickNameField", optional: true, fullWidth: true, label: nameLabelText, status: getTextFieldStatus(formErrors.cardNickName), statusMessage: (_a = formErrors.cardNickName) === null || _a === void 0 ? void 0 : _a.message, value: value || '', name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '' }),
                                    } }));
                            } })),
                    React.createElement("div", { className: "sui-gap-4 sui-pb-4 sui-w-full sm:sui-w-auto" },
                        React.createElement(Controller, { name: "cardNumber", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                const onChangeRevised = (event) => {
                                    if (cardType === CardType.GIFT_CARD) {
                                        onChange(getHyphenatedGiftCard(event.currentTarget.value || ''));
                                    }
                                    else {
                                        onChange(event);
                                    }
                                };
                                return (React.createElement(TextField, { id: "tenderNumberField", fullWidth: true, label: numberLabelText, status: getTextFieldStatus(formErrors.cardNumber), statusMessage: (_a = formErrors.cardNumber) === null || _a === void 0 ? void 0 : _a.message, value: value || '', name: name, onChange: onChangeRevised, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '' }),
                                    } }));
                            } })),
                    cardType === CardType.GIFT_CARD && (React.createElement("div", { className: "sui-flex sui-flex-row" },
                        React.createElement("div", { className: "sui-mr-2 sui-mt-1" },
                            React.createElement(WarningFilled, { color: "warning", size: iconSize })),
                        React.createElement("div", null,
                            React.createElement(Typography, { variant: "body-base", weight: "bold" }, "You can\u2019t select saved gift cards in checkout."),
                            React.createElement("div", { className: "sui-mt-2" },
                                React.createElement(Typography, { variant: "body-base" }, "Keep your gift card details to enter when checking out online."))))),
                    cardType === CardType.CHECKING_ACCOUNT && (React.createElement(Controller, { name: "routingNumber", control: control, render: ({ field }) => {
                            var _a;
                            const { ref, value, onChange, name } = field, rest = __rest(field, ["ref", "value", "onChange", "name"]);
                            return (React.createElement(TextField, { id: "accRoutingNumberField", fullWidth: true, label: "Account Routing Number*", status: getTextFieldStatus(formErrors.routingNumber), statusMessage: (_a = formErrors.routingNumber) === null || _a === void 0 ? void 0 : _a.message, value: value || '', onChange: onChange, name: name, InputProps: {
                                    inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '' }),
                                } }));
                        } }))),
                React.createElement(ModalFooter, { position: "end" },
                    React.createElement("div", { className: "sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto" },
                        React.createElement(Button, { variant: "secondary", onClick: hideModal }, "Cancel"),
                        React.createElement(LoadingButton, { "data-testid": "addTender", variant: "primary", loading: isLoading, type: "submit" }, buttonText)))))));
};
